/**
 * Relative time for audit entries — "12 min ago".
 *
 * The mock stored strings like `'12 min ago'` literally, which is fine for a
 * still frame and wrong the moment the data is real: those strings were minted
 * once and would age into lies. The API sends ISO-8601 timestamps, so the
 * phrasing is computed at render.
 *
 * Uses `Intl.RelativeTimeFormat` rather than hand-written thresholds so the
 * pluralisation is the platform's problem, not ours.
 */
const RELATIVE = new Intl.RelativeTimeFormat('en', { numeric: 'auto' });

const DIVISIONS: readonly { amount: number; unit: Intl.RelativeTimeFormatUnit }[] = [
    { amount: 60, unit: 'second' },
    { amount: 60, unit: 'minute' },
    { amount: 24, unit: 'hour' },
    { amount: 7, unit: 'day' },
    { amount: 4.34524, unit: 'week' },
    { amount: 12, unit: 'month' },
    { amount: Number.POSITIVE_INFINITY, unit: 'year' },
];

export function relativeTime(iso: string): string {
    const timestamp = Date.parse(iso);
    // A malformed timestamp should show the raw value rather than "Invalid Date"
    // — in an audit log the unparseable string is itself the useful evidence.
    if (Number.isNaN(timestamp)) return iso;

    let duration = (timestamp - Date.now()) / 1000;

    for (const division of DIVISIONS) {
        if (Math.abs(duration) < division.amount) {
            return RELATIVE.format(Math.round(duration), division.unit);
        }
        duration /= division.amount;
    }

    return iso;
}

/** Absolute rendering, for the `title` tooltip behind a relative label. */
export function absoluteTime(iso: string): string {
    const timestamp = Date.parse(iso);
    if (Number.isNaN(timestamp)) return iso;
    return new Date(timestamp).toLocaleString();
}
